import React from "react";
import { Tabs, Text } from "@mantine/core";

import { useMiddleware } from "@piqnt/runtime-core/react/ReactMount";
import { SpaceContext } from "../space";

import { getKey } from "@piqnt/runtime-space/drawing-model";

import { WatchVec2 } from "@piqnt/runtime-core/react/WatchValue";
import {
  Pair,
  PairKey,
  PairValue,
  Subtree,
  SubtreeBody,
  SubtreeHeader,
} from "@piqnt/runtime-core/react/Subtree";

import { LiveBody } from "./LiveBody";
import { LiveFixture } from "./LiveFixture";
import { LiveJoint } from "./LiveJoint";
import { JointCreate } from "./JointCreate";
import { BodyCreate } from "./BodyCreate";
import { ViewboxView } from "./ViewboxView";

export const SceneList = () => {
  const { context, emit } = useMiddleware<SpaceContext>();
  const world = context.draftScene.value?.world;

  if (!world) return null;

  const bodies = world.bodies || [];
  const joints = world.joints || [];

  return (
    <Tabs defaultValue="bodies">
      <Tabs.List>
        <Tabs.Tab value="bodies">
          <Text size="xs">Bodies</Text>
        </Tabs.Tab>
        <Tabs.Tab value="joints">
          <Text size="xs">Joints</Text>
        </Tabs.Tab>
      </Tabs.List>
      <Tabs.Panel value="bodies">
        <Subtree open>
          <SubtreeHeader>bodies {bodies.length}</SubtreeHeader>
          <SubtreeBody>
            {bodies.map((body) => (
              <Subtree key={getKey(body)}>
                <SubtreeHeader
                  onClick={() => emit("select-body", { body: getKey(body) })}
                >
                  <LiveBody body={body} />
                </SubtreeHeader>
                <SubtreeBody>
                  {body.fixtures.map((fixture) => (
                    <Subtree>
                      <SubtreeHeader
                        onClick={() =>
                          emit("select-fixture", {
                            fixture: getKey(fixture),
                            body: getKey(body),
                          })
                        }
                      >
                        <LiveFixture fixture={fixture} />
                      </SubtreeHeader>
                    </Subtree>
                  ))}
                </SubtreeBody>
              </Subtree>
            ))}
          </SubtreeBody>
        </Subtree>
      </Tabs.Panel>
      <Tabs.Panel value="joints">
        <Subtree open>
          <SubtreeHeader>joints {joints.length}</SubtreeHeader>
          <SubtreeBody>
            {joints.map((joint) => (
              <Subtree key={getKey(joint)}>
                <SubtreeHeader
                  onClick={() => emit("select-joint", { joint: getKey(joint) })}
                >
                  <LiveJoint joint={joint} />
                </SubtreeHeader>
              </Subtree>
            ))}
          </SubtreeBody>
        </Subtree>
      </Tabs.Panel>
    </Tabs>
  );
};

export const SceneEdit = () => {
  const { context, emit } = useMiddleware<SpaceContext>();
  const editable = context.editable.value;
  const world = context.draftScene.value?.world;

  if (!world) return null;

  return (
    <>
      <ViewboxView />
      <Subtree open>
        <SubtreeHeader>world</SubtreeHeader>
        <SubtreeBody>
          <Pair>
            <PairKey>gravity</PairKey>
            <PairValue>
              <WatchVec2
                editable={editable}
                setValue={(v) => emit("world-set-gravity", { gravity: v })}
                getValue={() => world.gravity}
              />
            </PairValue>
          </Pair>
        </SubtreeBody>
      </Subtree>
      <BodyCreate />
      <JointCreate />
    </>
  );
};
